import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import {
  Database, Search, ChevronLeft, ChevronRight, Loader2,
  Plus, Trash2, Edit, FileSpreadsheet, CreditCard, Receipt,
  X, BarChart3
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  FileSpreadsheet, CreditCard, Receipt, Database, BarChart3,
};

export default function BaseDatosFlujos() {
  const [collections, setCollections] = useState([]);
  const [activeTab, setActiveTab] = useState("");
  const [loading, setLoading] = useState(true);
  const [showCreateCol, setShowCreateCol] = useState(false);
  const [editingCol, setEditingCol] = useState(null);
  const [confirmDeleteCol, setConfirmDeleteCol] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchCollections = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/base-datos-flujos/colecciones`, getAuthHeaders());
      setCollections(res.data || []);
      if (res.data.length > 0 && !activeTab) {
        setActiveTab(res.data[0].id);
      }
    } catch {
      toast.error("Error cargando colecciones");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCollections(); }, [fetchCollections]);

  const handleDeleteCol = async () => {
    if (!confirmDeleteCol) return;
    try {
      await axios.delete(`${API}/api/base-datos-flujos/colecciones/${confirmDeleteCol}`, getAuthHeaders());
      toast.success("Colección eliminada");
      setConfirmDeleteCol(null);
      if (activeTab === confirmDeleteCol) setActiveTab("");
      fetchCollections();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error eliminando");
    }
  };

  const activeCollection = collections.find(c => c.id === activeTab);

  return (
    <div className="flex h-screen bg-slate-50">
      <Sidebar />
      <main className="flex-1 overflow-auto p-6 ml-0 md:ml-64">
        <div className="max-w-[95%] mx-auto space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-testid="bdf-title">
                <Database className="w-6 h-6 text-indigo-500" />
                Base de Datos Flujos
              </h1>
              <p className="text-sm text-slate-500 mt-1">Visualice la data importada por los flujos de automatización</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => { setEditingCol(null); setShowCreateCol(true); }} data-testid="add-collection-btn">
              <Plus className="w-4 h-4 mr-1" /> Nueva Pestaña
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : collections.length === 0 ? (
            <Card><CardContent className="py-12 text-center"><Database className="w-10 h-10 mx-auto text-slate-300 mb-3" /><p className="text-slate-500">No hay colecciones configuradas</p></CardContent></Card>
          ) : (
            <>
              {/* Tabs */}
              <div className="flex items-center gap-1 border-b overflow-x-auto pb-0">
                {collections.map(col => {
                  const Icon = ICON_MAP[col.icono] || FileSpreadsheet;
                  return (
                    <button
                      key={col.id}
                      onClick={() => setActiveTab(col.id)}
                      className={`group flex items-center gap-1.5 px-4 py-2.5 text-sm border-b-2 transition-colors whitespace-nowrap ${
                        activeTab === col.id
                          ? "border-indigo-500 text-indigo-600 font-medium bg-indigo-50/50"
                          : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                      }`}
                      data-testid={`tab-${col.id}`}
                    >
                      <Icon className="w-4 h-4" />
                      {col.nombre}
                      <Badge variant="secondary" className="text-[10px] ml-1 px-1.5">{col.total_registros}</Badge>
                      {/* Edit/Delete on hover */}
                      <span className="hidden group-hover:flex items-center gap-0.5 ml-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); setEditingCol(col); setShowCreateCol(true); }}
                          className="p-0.5 rounded hover:bg-slate-200"
                          title="Editar"
                        >
                          <Edit className="w-3 h-3 text-slate-400" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setConfirmDeleteCol(col.id); }}
                          className="p-0.5 rounded hover:bg-red-100"
                          title="Eliminar"
                        >
                          <Trash2 className="w-3 h-3 text-red-400" />
                        </button>
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Active Tab Content */}
              {activeCollection && (
                <CollectionGrid
                  key={activeTab}
                  collection={activeCollection}
                  getAuthHeaders={getAuthHeaders}
                />
              )}
            </>
          )}
        </div>

        {/* Create/Edit Collection Dialog */}
        <CollectionFormDialog
          open={showCreateCol}
          onOpenChange={setShowCreateCol}
          collection={editingCol}
          getAuthHeaders={getAuthHeaders}
          onSaved={() => { fetchCollections(); setShowCreateCol(false); }}
        />

        {/* Delete Confirm */}
        <AlertDialog open={!!confirmDeleteCol} onOpenChange={() => setConfirmDeleteCol(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Eliminar Colección</AlertDialogTitle>
              <AlertDialogDescription>Se eliminará la colección y todos sus datos. Esta acción no se puede deshacer.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteCol} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </main>
    </div>
  );
}


// ============== COLLECTION GRID ==============
function CollectionGrid({ collection, getAuthHeaders }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [confirmClear, setConfirmClear] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: "100" });
      if (search) params.set("search", search);
      const res = await axios.get(`${API}/api/base-datos-flujos/colecciones/${collection.id}/datos?${params}`, getAuthHeaders());
      setData(res.data);
    } catch {
      toast.error("Error cargando datos");
    } finally {
      setLoading(false);
    }
  }, [collection.id, page, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const handleClear = async () => {
    try {
      await axios.delete(`${API}/api/base-datos-flujos/colecciones/${collection.id}/datos`, getAuthHeaders());
      toast.success("Datos eliminados");
      setConfirmClear(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error eliminando datos");
    }
  };

  const columnas = data?.columnas || [];
  const records = data?.records || [];
  const total = data?.total || 0;
  const pages = data?.pages || 1;

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3">
        <form onSubmit={handleSearch} className="flex gap-2 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Buscar en esta colección..."
              className="pl-10 h-9 text-sm"
              data-testid="grid-search-input"
            />
          </div>
          <Button type="submit" size="sm" variant="outline" data-testid="grid-search-btn">Buscar</Button>
          {search && (
            <Button type="button" variant="ghost" size="sm" onClick={() => { setSearch(""); setSearchInput(""); setPage(1); }}>
              <X className="w-4 h-4" />
            </Button>
          )}
        </form>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <span>{total} registros</span>
          {total > 0 && (
            <Button variant="ghost" size="sm" className="text-red-500 text-xs" onClick={() => setConfirmClear(true)} data-testid="clear-data-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1" />
              Limpiar
            </Button>
          )}
        </div>
      </div>

      {/* Data Grid */}
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : records.length === 0 ? (
            <div className="py-16 text-center">
              <FileSpreadsheet className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 text-sm mb-1">
                {search ? "No se encontraron resultados" : "No hay datos en esta colección"}
              </p>
              {!search && (
                <p className="text-xs text-slate-400">
                  Configure un flujo de automatización con esta colección como destino para cargar datos.
                </p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-320px)] scrollbar-visible border rounded-md">
              <table style={{ minWidth: `${(columnas.length + 2) * 140}px` }} className="text-sm border-collapse">
                <thead>
                  <tr className="bg-slate-100 sticky top-0 z-10">
                    <th className="text-[10px] font-bold text-slate-600 w-12 text-center px-2 py-2 border-b sticky left-0 bg-slate-100 z-20">#</th>
                    {columnas.map(col => (
                      <th key={col} className="text-[10px] font-bold text-slate-600 whitespace-nowrap px-3 py-2 border-b text-left">{col}</th>
                    ))}
                    <th className="text-[10px] font-bold text-slate-400 whitespace-nowrap px-3 py-2 border-b text-left">Flujo</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((record, idx) => (
                    <tr key={idx} className="hover:bg-blue-50/30 transition-colors border-b" data-testid={`grid-row-${idx}`}>
                      <td className="text-[10px] text-slate-400 text-center px-2 py-1.5 sticky left-0 bg-white z-20 font-mono border-r">{record._row_number || (page - 1) * 100 + idx + 1}</td>
                      {columnas.map(col => (
                        <td key={col} className="text-xs text-slate-700 whitespace-nowrap px-3 py-1.5" title={record[col] != null ? String(record[col]) : ""}>
                          {record[col] != null ? String(record[col]) : ""}
                        </td>
                      ))}
                      <td className="text-[10px] text-slate-400 whitespace-nowrap px-3 py-1.5">{record._flow_nombre || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {total > 0 && (
            <div className="flex items-center justify-between px-4 py-2.5 border-t bg-slate-50">
              <p className="text-xs text-slate-500">
                Mostrando {(page - 1) * 100 + 1}-{Math.min(page * 100, total)} de {total}
              </p>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" className="h-7 text-xs" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                  <ChevronLeft className="w-3.5 h-3.5" />
                </Button>
                <span className="text-xs text-slate-500 px-2">{page} / {pages}</span>
                <Button variant="outline" size="sm" className="h-7 text-xs" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>
                  <ChevronRight className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Clear Confirm */}
      <AlertDialog open={confirmClear} onOpenChange={setConfirmClear}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Limpiar Datos</AlertDialogTitle>
            <AlertDialogDescription>Se eliminarán todos los registros de "{collection.nombre}". Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleClear} className="bg-red-600 hover:bg-red-700">Eliminar Todo</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


// ============== COLLECTION FORM DIALOG ==============
function CollectionFormDialog({ open, onOpenChange, collection, getAuthHeaders, onSaved }) {
  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [icono, setIcono] = useState("FileSpreadsheet");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (collection) {
        setNombre(collection.nombre || "");
        setDescripcion(collection.descripcion || "");
        setIcono(collection.icono || "FileSpreadsheet");
      } else {
        setNombre(""); setDescripcion(""); setIcono("FileSpreadsheet");
      }
    }
  }, [open, collection]);

  const handleSave = async () => {
    if (!nombre.trim()) { toast.error("El nombre es obligatorio"); return; }
    setSaving(true);
    try {
      if (collection) {
        await axios.put(`${API}/api/base-datos-flujos/colecciones/${collection.id}`, { nombre, descripcion, icono }, getAuthHeaders());
        toast.success("Colección actualizada");
      } else {
        await axios.post(`${API}/api/base-datos-flujos/colecciones`, { nombre, descripcion, icono }, getAuthHeaders());
        toast.success("Colección creada");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  const iconOptions = [
    { value: "FileSpreadsheet", label: "Hoja de cálculo", Icon: FileSpreadsheet },
    { value: "Receipt", label: "Recibo/Cobro", Icon: Receipt },
    { value: "CreditCard", label: "Factura/Crédito", Icon: CreditCard },
    { value: "Database", label: "Base de datos", Icon: Database },
    { value: "BarChart3", label: "Reporte", Icon: BarChart3 },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="collection-form-dialog">
        <DialogHeader>
          <DialogTitle>{collection ? "Editar Pestaña" : "Nueva Pestaña"}</DialogTitle>
          <DialogDescription>Las pestañas organizan los datos por tipo de documento.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Nombre *</Label>
            <Input value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Ej: Listado Póliza" data-testid="col-name-input" />
          </div>
          <div>
            <Label>Descripción</Label>
            <Input value={descripcion} onChange={e => setDescripcion(e.target.value)} placeholder="Descripción opcional" />
          </div>
          <div>
            <Label>Icono</Label>
            <div className="flex gap-2 mt-1">
              {iconOptions.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setIcono(opt.value)}
                  className={`p-2 rounded-lg border transition-colors ${icono === opt.value ? "border-indigo-500 bg-indigo-50" : "border-slate-200 hover:border-slate-400"}`}
                  title={opt.label}
                >
                  <opt.Icon className={`w-5 h-5 ${icono === opt.value ? "text-indigo-600" : "text-slate-500"}`} />
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-col-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {collection ? "Guardar" : "Crear"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
